// frontend/src/services/localRecording/db.js
//
// Lokal-först-lagring för inspelning. Varje ljudchunk skrivs hit FÖRST och
// raderas först när servern kvitterat (2xx). Överlever omladdning, krasch och
// servernedgång. Rå IndexedDB (inga extra beroenden).

const DB_NAME = 'aion_local_recording';
const DB_VERSION = 1;
const STORE_SESSIONS = 'sessions';
const STORE_CHUNKS = 'chunks';

let dbPromise = null;

// Öppna (och migrera) databasen. Cachad singleton.
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'client_session_id' });
      }
      if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
        const chunks = db.createObjectStore(STORE_CHUNKS, { keyPath: 'id' });
        chunks.createIndex('by_session', 'client_session_id', { unique: false });
        chunks.createIndex('by_state', 'upload_state', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
  return dbPromise;
}

// Kör en transaktion och resolva när den committar (inte bara när requesten är klar).
async function tx(storeNames, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeNames, mode);
    let result;
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('tx aborted'));
    // fn får sätta `result` via retur; requests körs inom t
    Promise.resolve(fn(t)).then((r) => { result = r; }).catch(reject);
  });
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function chunkId(clientSessionId, chunkIndex) {
  return `${clientSessionId}#${chunkIndex}`;
}

// ---- Sessioner ------------------------------------------------------------

// Skapa/uppdatera en lokal session.
export async function putSession(session) {
  return tx(STORE_SESSIONS, 'readwrite', (t) =>
    reqToPromise(t.objectStore(STORE_SESSIONS).put(session))
  );
}

export async function getSession(clientSessionId) {
  return tx(STORE_SESSIONS, 'readonly', (t) =>
    reqToPromise(t.objectStore(STORE_SESSIONS).get(clientSessionId))
  );
}

export async function getAllSessions() {
  return tx(STORE_SESSIONS, 'readonly', (t) =>
    reqToPromise(t.objectStore(STORE_SESSIONS).getAll())
  );
}

// Sätt server-id när upsert-endpointen skapat/hittat server-sessionen.
export async function setServerSessionId(clientSessionId, serverSessionId) {
  return tx(STORE_SESSIONS, 'readwrite', async (t) => {
    const store = t.objectStore(STORE_SESSIONS);
    const s = await reqToPromise(store.get(clientSessionId));
    if (!s) return;
    s.server_session_id = serverSessionId;
    await reqToPromise(store.put(s));
  });
}

// Uppdatera mall/namn på en pågående lokal session (användaren kan välja mall
// eller namnge sessionen EFTER att inspelningen startat — särskilt viktigt när
// servern var nere vid start och sessionen skapas lazy från dessa värden).
export async function updateSessionMeta(clientSessionId, { templateId, sessionName } = {}) {
  return tx(STORE_SESSIONS, 'readwrite', async (t) => {
    const store = t.objectStore(STORE_SESSIONS);
    const s = await reqToPromise(store.get(clientSessionId));
    if (!s) return;
    if (templateId != null) s.template_id = templateId;
    if (sessionName != null) s.session_name = sessionName;
    await reqToPromise(store.put(s));
  });
}

// Markera en session som klar lokalt (avsluta). Finish mot servern sker först
// när kön för sessionen är tömd.
export async function markSessionFinished(clientSessionId) {
  return tx(STORE_SESSIONS, 'readwrite', async (t) => {
    const store = t.objectStore(STORE_SESSIONS);
    const s = await reqToPromise(store.get(clientSessionId));
    if (!s) return;
    s.status = 'finished';
    s.finished_at = Date.now();
    await reqToPromise(store.put(s));
  });
}

// ---- Chunkar --------------------------------------------------------------

// Lägg en chunk i kön. `blob` lagras direkt (IndexedDB stödjer Blob).
export async function putChunk({ clientSessionId, chunkIndex, blob, isFinal }) {
  const record = {
    id: chunkId(clientSessionId, chunkIndex),
    client_session_id: clientSessionId,
    chunk_index: chunkIndex,
    blob,
    is_final: !!isFinal,
    created_at: Date.now(),
    upload_state: 'pending', // pending | uploading | confirmed | permanent_failure
    attempts: 0,
    last_error: null,
    next_attempt_at: 0,
  };
  return tx(STORE_CHUNKS, 'readwrite', (t) =>
    reqToPromise(t.objectStore(STORE_CHUNKS).put(record))
  );
}

export async function getChunk(id) {
  return tx(STORE_CHUNKS, 'readonly', (t) =>
    reqToPromise(t.objectStore(STORE_CHUNKS).get(id))
  );
}

// Alla chunkar för en session, sorterade i chunk_index-ordning.
export async function getChunksForSession(clientSessionId) {
  const all = await tx(STORE_CHUNKS, 'readonly', (t) =>
    reqToPromise(t.objectStore(STORE_CHUNKS).index('by_session').getAll(clientSessionId))
  );
  return all.sort((a, b) => a.chunk_index - b.chunk_index);
}

// Chunkar som ännu inte är bekräftade (pending/uploading/permanent_failure kvar).
// Sorterade äldst först (session, sedan chunk_index).
export async function getUnconfirmedChunks() {
  const all = await tx(STORE_CHUNKS, 'readonly', (t) =>
    reqToPromise(t.objectStore(STORE_CHUNKS).getAll())
  );
  return all
    .filter((c) => c.upload_state !== 'confirmed')
    .sort((a, b) =>
      a.client_session_id === b.client_session_id
        ? a.chunk_index - b.chunk_index
        : a.created_at - b.created_at
    );
}

// Uppdatera valda fält på en chunk (state, attempts, last_error, next_attempt_at).
export async function updateChunk(id, patch) {
  return tx(STORE_CHUNKS, 'readwrite', async (t) => {
    const store = t.objectStore(STORE_CHUNKS);
    const c = await reqToPromise(store.get(id));
    if (!c) return;
    Object.assign(c, patch);
    await reqToPromise(store.put(c));
  });
}

export async function deleteChunk(id) {
  return tx(STORE_CHUNKS, 'readwrite', (t) =>
    reqToPromise(t.objectStore(STORE_CHUNKS).delete(id))
  );
}

// Radera en session och alla dess chunkar (efter lyckad complete).
export async function deleteSessionAndChunks(clientSessionId) {
  const chunks = await getChunksForSession(clientSessionId);
  return tx([STORE_SESSIONS, STORE_CHUNKS], 'readwrite', async (t) => {
    const cs = t.objectStore(STORE_CHUNKS);
    for (const c of chunks) await reqToPromise(cs.delete(c.id));
    await reqToPromise(t.objectStore(STORE_SESSIONS).delete(clientSessionId));
  });
}

// Antal ej bekräftade chunkar per client_session_id — för Mina sessioner-badge.
export async function getPendingCountsBySession() {
  const unconfirmed = await getUnconfirmedChunks();
  const counts = {};
  for (const c of unconfirmed) {
    if (!counts[c.client_session_id]) {
      counts[c.client_session_id] = { pending: 0, permanent_failure: 0 };
    }
    if (c.upload_state === 'permanent_failure') {
      counts[c.client_session_id].permanent_failure += 1;
    } else {
      counts[c.client_session_id].pending += 1;
    }
  }
  return counts;
}
